import { resolveApiBaseUrl } from '../utils/apiBase';

const API_BASE = resolveApiBaseUrl();

async function errorMessage(res) {
  try {
    const body = await res.json();
    if (body?.message) return body.message;
    if (body?.error) return body.error;
  } catch {
    // ignore
  }
  return `${res.status} ${res.statusText}`;
}

let cachedCsrfToken = null;

/** Fetch CSRF token for mutating requests (header + cookie). */
export async function ensureCsrfCookie() {
  const res = await fetch(`${API_BASE}/v1/auth/csrf`, { credentials: 'include' });
  if (res.ok) {
    const body = await res.json();
    cachedCsrfToken = body?.token || null;
  }
  return cachedCsrfToken;
}

export function getCsrfToken() {
  return cachedCsrfToken || readXsrfTokenFromDocument();
}

export function xsrfHeaders() {
  const token = getCsrfToken();
  return token ? { 'X-XSRF-TOKEN': token } : {};
}

function readXsrfTokenFromDocument() {
  const part = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('XSRF-TOKEN='));
  if (!part) return null;
  const value = part.slice('XSRF-TOKEN='.length);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export async function fetchAuthStatus() {
  const res = await fetch(`${API_BASE}/v1/auth/status`, { credentials: 'include' });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

/** Returns null when anonymous (204 or legacy 401). */
export async function fetchMeOptional() {
  const res = await fetch(`${API_BASE}/v1/auth/me`, { credentials: 'include' });
  if (res.status === 204 || res.status === 401) return null;
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

export function hasSessionCookie() {
  if (typeof document === 'undefined') return false;
  return document.cookie.split(';').some((c) => c.trim().startsWith('JSESSIONID='));
}

export async function postLogin(username, password) {
  const res = await fetch(`${API_BASE}/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  await ensureCsrfCookie();
  return res.json();
}

export async function postLogout() {
  const res = await fetch(`${API_BASE}/v1/auth/logout`, {
    method: 'POST',
    headers: xsrfHeaders(),
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await errorMessage(res));
}
